import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import CheckIcon from '~icons/lucide/check';
import ChevronDownIcon from '~icons/lucide/chevron-down';
import MaximizeIcon from '~icons/lucide/maximize-2';
import MicIcon from '~icons/lucide/mic';
import MicOffIcon from '~icons/lucide/mic-off';
import MinimizeIcon from '~icons/lucide/minimize-2';
import PhoneOffIcon from '~icons/lucide/phone-off';
import PipIcon from '~icons/lucide/picture-in-picture-2';
import SettingsIcon from '~icons/lucide/settings-2';
import VolumeXIcon from '~icons/lucide/volume-x';
import CloseIcon from '~icons/lucide/x';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useChatSettingsStore } from '#modules/chat/model/chatSettingsStore';
import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import { MicLevelMeter } from '#features/media/MicLevelMeter';
import { CallStage } from '#features/calls/components/CallOverlay/CallStage';
import { RingScreen } from '#features/calls/components/CallOverlay/RingScreen';
import { useCallActions } from '#features/calls/hooks/useCallActions';
import { useCallRoom, type CallDevices } from '#features/calls/hooks/useCallRoom';
import { useCallStore } from '#features/calls/model/callStore';
import { toCallAvatarUrl } from '#features/calls/utils/callAvatar';
import {
  isDocumentPipSupported,
  openCallPipWindow,
  type CallPipHandle,
} from '#features/calls/utils/callPipWindow';
import { startRingtone, stopRingtone } from '#features/calls/utils/callRingtone';
import { IS_IOS_AUDIO_ROUTED } from '#features/media-window/audioRouting';
import { CORNER_PROPS, MOBILE_BAR_BOTTOM, MOBILE_BAR_TOP } from '#features/media-window/model';
import { useCornerDrag } from '#features/media-window/useCornerDrag';
import { dismissCallNotification } from '#features/notifications/utils/deviceSettings';
import { onWsReconnect } from '#features/realtime/hooks/useRealtimeNotifications';
import { Avatar } from '#features/ui/components/Avatar';
import { Button } from '#features/ui/components/Button';
import { IconButton } from '#features/ui/components/IconButton';
import { Portal } from '#features/ui/components/Portal';
import { useVoiceStore } from '#features/voice/model/voiceStore';
import { envConfig } from '#constants/envConfig';
import { useBottomSpaceVar } from '#hooks/useBottomSpaceVar';
import { formatElapsed } from '#features/date';

/**
 * Long enough to read the note *and* act on it: the note now offers a call back, and 2.5s
 * was under the time it takes to notice a button appeared.
 */
const ENDED_DISMISS_MS = 6_000;

/**
 * Frosted materials are cheap on iOS and on desktop, and expensive in Android's WebView when
 * they sit over a scrolling feed — which is exactly where the mini pill lives. On native the
 * pill is over an opaque app background anyway, so the blur buys nothing there.
 */
const PILL_BACKDROP = envConfig.IS_NATIVE ? undefined : 'blur(20px) saturate(1.3)';

/**
 * Панели «звонок на другом устройстве» сворачиваться некуда — свёрнутого вида у неё нет, — поэтому
 * рывок вниз от нижнего края просто оставляет её на месте. Константа, а не стрелка в теле: жест
 * запоминается по ссылкам на колбэки, и новая функция на каждый рендер пересобирала бы обработчик.
 */
const NO_COLLAPSE = () => {};

const formatDuration = (fromMs: number, nowMs: number): string =>
  formatElapsed((nowMs - fromMs) / 1000);

const useCallDuration = (acceptedAt: number | null, enabled: boolean): string => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [enabled]);

  if (!enabled || !acceptedAt) {
    return '00:00';
  }
  return formatDuration(acceptedAt, now);
};

type DeviceMenuProps = {
  devices: CallDevices;
  onSwitch: (kind: 'audioinput' | 'audiooutput' | 'videoinput', deviceId: string) => void;
  onOpen: () => void;
  /**
   * Панель открывает не только своя шестерёнка: выдав разрешение по нажатию на кнопку с меткой,
   * человек сразу оказывается перед списком устройств — «одним движением», как в Meet.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Microphone / speaker / camera pickers (speaker hidden where setSinkId is
 * unsupported). Deliberately NOT a portalled Chakra menu: the call widget is a
 * fixed/fullscreen overlay and a body-portalled positioner kept anchoring wrong
 * (detached on desktop, off-screen on mobile). A plain absolute panel inside
 * the widget is bullet-proof in every window mode.
 */
const DeviceMenu = ({ devices, onSwitch, onOpen, open, onOpenChange }: DeviceMenuProps) => {
  const { t } = useLingui();
  const setOpen = onOpenChange;
  const rootRef = useRef<HTMLDivElement | null>(null);

  // The in-widget layer below only catches clicks landing on the widget itself; a click anywhere
  // else on the page must dismiss the panel too, which is what everyone expects from a dropdown.
  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target || rootRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, setOpen]);

  const section = (
    label: string,
    kind: 'audioinput' | 'audiooutput' | 'videoinput',
    options: CallDevices['microphones'],
    activeId: string | null
  ) => (
    <>
      <Text textStyle="caption" color="whiteAlpha.500" px={3} pt={2} pb={1}>
        {label}
      </Text>
      {options.length === 0 && (
        <Text textStyle="caption" color="whiteAlpha.400" px={3} pb={1}>
          {t`No devices found`}
        </Text>
      )}
      {options.map((device) => (
        <HStack
          key={`${kind}:${device.deviceId}`}
          as="button"
          w="100%"
          textAlign="left"
          px={3}
          py={2}
          gap={2}
          cursor="pointer"
          _hover={{ bg: 'bg.emphasized' }}
          onClick={() => {
            onSwitch(kind, device.deviceId);
            setOpen(false);
          }}
        >
          <Box w="16px" color="green.400" flexShrink={0}>
            {device.deviceId === activeId ? <CheckIcon /> : null}
          </Box>
          <Text textStyle="caption" color="whiteAlpha.900" lineClamp={1}>
            {device.label}
          </Text>
        </HStack>
      ))}
    </>
  );

  return (
    // display:contents — a box only for `contains()` checks, invisible to layout and to the
    // absolute positioning of the panel below.
    <Box ref={rootRef} display="contents">
      <IconButton
        aria-label={t`Audio and video settings`}
        size="sm"
        minW="40px"
        h="40px"
        borderRadius="lg"
        bg={open ? 'bg.emphasized' : 'transparent'}
        color="fg.secondary"
        _hover={{ bg: 'bg.emphasized', color: 'fg' }}
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) {
            onOpen();
          }
        }}
      >
        <SettingsIcon />
      </IconButton>
      {open && (
        <>
          {/* Invisible click-away layer, scoped to the widget. */}
          <Box position="absolute" inset={0} zIndex={4} onClick={() => setOpen(false)} />
          <Box
            position="absolute"
            top="48px"
            right="10px"
            zIndex={5}
            w="280px"
            maxH="min(340px, calc(100% - 60px))"
            overflowY="auto"
            bg="bg.card"
            borderWidth="1px"
            borderColor="border"
            borderRadius="xl"
            boxShadow="2xl"
            pb={2}
          >
            {IS_IOS_AUDIO_ROUTED ? (
              <Text textStyle="caption" color="whiteAlpha.500" px={3} pt={2}>
                {t`Audio device is managed by iOS - switch it in Control Center (audio output tile).`}
              </Text>
            ) : (
              <>
                {section(
                  t`Microphone`,
                  'audioinput',
                  devices.microphones,
                  devices.activeMicrophoneId
                )}
                {/* Полоска ровно там же, где в голосе сессии: «меня слышно?» чинят в одном месте. */}
                <MicLevelMeter enabled={open} deviceId={devices.activeMicrophoneId ?? undefined} />
              </>
            )}
            {devices.speakerSelectionSupported &&
              section(t`Speaker`, 'audiooutput', devices.speakers, devices.activeSpeakerId)}
            {section(t`Camera`, 'videoinput', devices.cameras, devices.activeCameraId)}
          </Box>
        </>
      )}
    </Box>
  );
};

/**
 * Global 1:1 call UI: incoming/outgoing ring, in-call window (fullscreen on mobile,
 * draggable-to-corner floating window on desktop, collapsible into a mini pill) and
 * a brief "call ended" note. Mounted once in the locale layout next to the WS hook.
 * Media (SDP/ICE/TURN/reconnects) is handled by LiveKit via useCallRoom.
 */
export const CallOverlay = () => {
  const { t } = useLingui();
  const isAuth = useIsAuth();
  const { profile } = useProfile();
  const selfAvatar = toCallAvatarUrl(profile?.avatar);
  const phase = useCallStore((s) => s.phase);
  const call = useCallStore((s) => s.call);
  const endReason = useCallStore((s) => s.endReason);
  const micEnabled = useCallStore((s) => s.micEnabled);
  const camEnabled = useCallStore((s) => s.camEnabled);
  const windowMode = useCallStore((s) => s.windowMode);
  // Единственное, что звонок знает про голос сессии: идёт ли там разговор прямо сейчас. Ответ на
  // звонок из комнаты выводит (арбитраж живёт в features/voice), а до ответа ринг обязан вести
  // себя скромнее — иначе он отбирает управление чужим разговором.
  const inVoiceRoom = useVoiceStore((s) => s.phase !== 'idle');
  const corner = useCallStore((s) => s.corner);
  const mobileAnchor = useCallStore((s) => s.mobileAnchor);
  const setMicEnabled = useCallStore((s) => s.setMicEnabled);
  const setCamEnabled = useCallStore((s) => s.setCamEnabled);
  const setWindowMode = useCallStore((s) => s.setWindowMode);
  const setCorner = useCallStore((s) => s.setCorner);
  const setMobileAnchor = useCallStore((s) => s.setMobileAnchor);

  const { answer, claim, dismissHere, hangup, resync, start } = useCallActions();
  const room = useCallRoom();
  const {
    remoteAudioRef,
    connectionState,
    peerGone,
    audioBlocked,
    unblockAudio,
    devices,
    switchDevice,
    refreshDevices,
    attachPipVideo,
    detachPipVideo,
  } = room;

  const duration = useCallDuration(call?.acceptedAt ?? null, phase === 'active');
  const windowRef = useRef<HTMLDivElement | null>(null);
  // The mini pill needs both: a ref for the drag gesture and a node in state, so the effect that
  // publishes the space it takes re-runs when the pill appears and disappears.
  const pillRef = useRef<HTMLDivElement | null>(null);
  const [pillNode, setPillNode] = useState<HTMLDivElement | null>(null);
  const attachPill = useCallback((node: HTMLDivElement | null) => {
    pillRef.current = node;
    setPillNode(node);
  }, []);
  // Панель «звонок идёт на другом устройстве» переносится тем же жестом, что и пилюля, поэтому
  // ей нужна своя ссылка на узел: жест двигает именно тот элемент, за который взялись.
  const elsewhereRef = useRef<HTMLDivElement | null>(null);
  const pipRef = useRef<CallPipHandle | null>(null);
  const [endedHovered, setEndedHovered] = useState(false);
  // Список устройств живёт в шапке, а открывать его умеет и кнопка микрофона из панели снизу —
  // поэтому состояние держит общий родитель, а не сама панель.
  const [deviceMenuOpen, setDeviceMenuOpen] = useState(false);
  // The duration timer stops with the call, so the "ended" note would report 00:00 unless
  // the last value is kept.
  const lastDurationRef = useRef('00:00');

  /**
   * Answering a video call without the camera is still answering: the ring screen offers it
   * as a separate action, and all it means is that the local camera track starts off.
   */
  const handleAnswer = useCallback(
    (options?: { video: boolean }) => {
      if (options && !options.video) {
        setCamEnabled(false);
      }
      void answer();
    },
    [answer, setCamEnabled]
  );

  // Grip gestures: on mobile a downward swipe collapses the call into the mini
  // pill (and never reaches the browser's pull-to-refresh); on desktop the
  // window follows the pointer live and snaps to the nearest corner on release.
  // Fullscreen isn't draggable, hence the mode check.
  //
  // No `onAnchor` here: on a phone the expanded call is the whole screen (`inset: 0` below), so
  // there is no edge for it to sit at. Only the pill moves between edges.
  const onHeaderPointerDown = useCornerDrag({
    ref: windowRef,
    canDrag: windowMode === 'window',
    onCollapse: () => setWindowMode('mini'),
    onCorner: setCorner,
  });

  /**
   * The pill has its own gesture. It used to have none, and that was the whole problem: on a phone
   * the pill sits at the bottom edge, which in a chat thread is exactly where the message composer
   * is. The call could not be moved and hiding it is not an option — closing it would mean hanging
   * up — so the only way to write was to end the conversation.
   */
  const onPillPointerDown = useCornerDrag({
    ref: pillRef,
    canDrag: false,
    anchor: mobileAnchor,
    onCollapse: () => setWindowMode('mini'),
    onCorner: setCorner,
    onAnchor: setMobileAnchor,
  });

  /**
   * Та же ручка у панели «звонок идёт на другом устройстве». Панель встаёт на место пилюли — в
   * чате это ровно поле ввода, — но подвинуть её было нечем: оставалось либо оборвать разговор,
   * который в эту секунду идёт на другом устройстве, либо убрать панель совсем и вместе с ней
   * единственную кнопку «перенести сюда».
   */
  const onElsewherePointerDown = useCornerDrag({
    ref: elsewhereRef,
    canDrag: false,
    anchor: mobileAnchor,
    onCollapse: NO_COLLAPSE,
    onCorner: setCorner,
    onAnchor: setMobileAnchor,
  });

  // The page leaves room for the pill the same way it does for the bottom navigation — but only
  // while the pill is actually at the bottom. Raised to the top edge it covers nothing.
  useBottomSpaceVar(
    '--voice-bar-space',
    phase === 'active' && windowMode === 'mini' && mobileAnchor === 'bottom' ? pillNode : null
  );

  // Meet-style always-on-top widget (Document PiP, Chrome/Edge desktop). The PiP
  // window mirrors the peer's stream and basic controls; audio keeps playing in
  // this tab. Closed automatically when the call ends.
  const openPip = useCallback(async () => {
    const currentCall = useCallStore.getState().call;
    if (pipRef.current || !currentCall) {
      return;
    }
    const peerName = currentCall.peer.displayName || t`Player`;
    const handle = await openCallPipWindow({
      peerName,
      peerAvatarUrl: currentCall.peer.avatar,
      attachVideo: attachPipVideo,
      detachVideo: detachPipVideo,
      getDurationLabel: () => {
        const acceptedAt = useCallStore.getState().call?.acceptedAt;
        return acceptedAt ? formatDuration(acceptedAt, Date.now()) : '00:00';
      },
      isMicEnabled: () => useCallStore.getState().micEnabled,
      onToggleMic: () => {
        const state = useCallStore.getState();
        state.setMicEnabled(!state.micEnabled);
      },
      onHangup: () => hangup(),
      onExpand: () => window.focus(),
      onClosed: () => {
        pipRef.current = null;
      },
      labels: {
        mic: t`Microphone`,
        hangup: t`End call`,
        expand: t`Back to Teamplay`,
        sources: {
          remoteScreen: t`${peerName} - screen`,
          remoteCam: peerName,
          localScreen: t`You - screen`,
          localCam: t`You`,
        },
      },
    });
    pipRef.current = handle;
  }, [attachPipVideo, detachPipVideo, hangup, t]);

  useEffect(() => {
    if (phase !== 'active' && pipRef.current) {
      pipRef.current.close();
      pipRef.current = null;
    }
  }, [phase]);

  // Chrome auto-PiP: with this media-session handler registered, switching to
  // another tab during a call pops the widget automatically (user permission
  // permitting); unsupported action names just throw and are ignored.
  useEffect(() => {
    if (phase !== 'active' || !isDocumentPipSupported() || !('mediaSession' in navigator)) {
      return;
    }
    const action = 'enterpictureinpicture' as MediaSessionAction;
    try {
      navigator.mediaSession.setActionHandler(action, () => void openPip());
    } catch {
      return;
    }
    return () => {
      try {
        navigator.mediaSession.setActionHandler(action, null);
      } catch {
        // ignore
      }
    };
  }, [phase, openPip]);

  // Ring while a call is ringing (respects the global sound toggle and the per-chat mute).
  useEffect(() => {
    const { soundEnabled, isChatMuted } = useChatSettingsStore.getState();
    /**
     * Заглушённый чат молчит и звонком тоже: «без звука» обязано означать тишину от этого
     * человека целиком, иначе мьют выглядит выборочным враньём. Исходящего гудка это не касается —
     * звоню я сам, и тишина в ответ читается как поломка микрофона или связи.
     */
    const silent = !soundEnabled || isChatMuted(call?.chatId);

    if (phase === 'incoming') {
      // Once this overlay is on screen it owns the call — including the sound. The system
      // notification that woke the phone is the other ringtone, so it is silenced only after
      // ours is audibly playing: dismissing it first left a call ringing in total silence.
      const ringingCallId = call?.callId;
      // While the phone is locked or the app is in the background, the system notification is
      // the ring — it is also what wakes the screen. We only take the sound over once this
      // screen is actually visible, and only then silence the notification; doing it earlier
      // left the phone dark and, before that, silent.
      const ring = () => {
        if (silent) {
          // Наш звонок молчит — системное уведомление обязано замолчать тоже, иначе на телефоне
          // мьют не слышно вовсе: звонить продолжит операционная система. Гасим ровно там же, где
          // и обычно, — когда экран входящего уже виден и уведомление лишь дублирует его.
          if (ringingCallId) {
            void dismissCallNotification(ringingCallId);
          }
          return;
        }
        void startRingtone('incoming').then((playing) => {
          if (playing && ringingCallId) {
            void dismissCallNotification(ringingCallId);
          }
          return playing;
        });
      };
      if (document.hidden) {
        document.addEventListener('visibilitychange', ring, { once: true });
      } else {
        ring();
      }
      return () => {
        document.removeEventListener('visibilitychange', ring);
        stopRingtone();
      };
    }
    if (phase === 'outgoing') {
      if (soundEnabled) {
        void startRingtone('outgoing');
      }
      return () => stopRingtone();
    }
    stopRingtone();
  }, [phase, call?.callId, call?.chatId]);

  useEffect(() => {
    if (phase === 'active') {
      lastDurationRef.current = duration;
    }
  }, [phase, duration]);

  // The note can unmount from under the pointer — clicking its own "Call back" is exactly
  // that — and no mouseleave ever arrives. Without this reset the hover flag stays latched
  // and every later note hangs around forever.
  useEffect(() => {
    if (phase !== 'ended') {
      setEndedHovered(false);
    }
  }, [phase]);

  // Auto-dismiss the "ended" note — held open while the pointer is on it, so the call-back
  // button cannot disappear from under the cursor reaching for it.
  useEffect(() => {
    if (phase !== 'ended' || endedHovered) {
      return;
    }
    const timer = window.setTimeout(() => useCallStore.getState().reset(), ENDED_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [phase, endedHovered]);

  // State resync: on mount (reload mid-call) and after every WS reconnect.
  useEffect(() => {
    if (!isAuth) {
      return;
    }
    void resync();
    return onWsReconnect(() => void resync());
  }, [isAuth, resync]);

  // iOS Safari freezes background tabs mid-call-setup: the user accepts, the tab
  // gets suspended before room.connect, and minutes later the thawed tab replays a
  // stale connect (seen in the wild as /rtc/validate with a long-dead room token).
  // Resync against the server the moment the tab becomes visible again.
  useEffect(() => {
    if (!isAuth) {
      return;
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void resync();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAuth, resync]);

  if (!isAuth || phase === 'idle' || !call) {
    return null;
  }

  const peerName = call.peer.displayName || t`Player`;

  const endedText = (() => {
    switch (endReason) {
      case 'declined':
        return t`Call declined`;
      case 'no_answer':
        return t`No answer`;
      case 'missed':
      case 'cancelled':
        return t`Call ended`;
      case 'answered_elsewhere':
        return t`Answered on another device`;
      case 'peer_left':
        return t`${peerName} left the call`;
      case 'connect_failed':
        return t`Could not connect to the call`;
      default:
        return t`Call ended`;
    }
  })();

  const statusText =
    connectionState === 'reconnecting'
      ? t`Reconnecting…`
      : connectionState === 'connecting'
        ? t`Connecting…`
        : peerGone
          ? t`${peerName} is reconnecting…`
          : duration;

  return (
    <Portal>
      {/* Hidden sink for remote audio — must exist in the DOM to be audible.
          Live WebRTC has no caption source; the empty track satisfies a11y. */}
      <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }}>
        <track kind="captions" />
      </audio>

      {(phase === 'incoming' || phase === 'outgoing') && (
        <RingScreen
          call={call}
          phase={phase}
          peerName={peerName}
          onAnswer={handleAnswer}
          onHangup={() => hangup(phase === 'incoming' ? 'declined' : 'cancelled')}
          // Человек уже разговаривает с командой — тогда звонок просится, а не врывается.
          compact={inVoiceRoom}
        />
      )}

      {phase === 'active' && windowMode === 'mini' && (
        <HStack
          ref={attachPill}
          position="fixed"
          // Which edge on a phone is the person's choice, made with a drag; on desktop the pill
          // keeps its corner, where it overlaps nothing. `auto` on the opposite side is explicit —
          // without it the pill stretches from top to bottom.
          top={{ base: mobileAnchor === 'top' ? MOBILE_BAR_TOP : 'auto', md: 'auto' }}
          bottom={{ base: mobileAnchor === 'top' ? 'auto' : MOBILE_BAR_BOTTOM, md: 4 }}
          right={{ base: 4, md: 4 }}
          left={{ base: 4, md: 'auto' }}
          zIndex="modal"
          bg="bg.surface/92"
          backdropFilter={PILL_BACKDROP}
          borderRadius="2xl"
          borderWidth="1px"
          borderColor="border"
          boxShadow="xl"
          px={2.5}
          py={2}
          gap={2.5}
        >
          {/* Avatar and name double as the grip: that is what the pill is dragged by, away from
              the bottom edge where it lands on the message composer. Not the whole pill —
              `preventDefault` on pointerdown would take the taps away from the mic and hang-up
              buttons next to it. */}
          <HStack
            gap={2.5}
            minW={0}
            flex={1}
            userSelect="none"
            onPointerDown={onPillPointerDown}
            css={{ touchAction: 'none', overscrollBehavior: 'contain' }}
          >
            <Avatar
              size="xs"
              src={call.peer.avatar}
              name={peerName}
              borderRadius="lg"
              css={{ '--avatar-radius': 'var(--chakra-radii-lg)' }}
            />
            <Stack gap={0} minW={0} flex={1}>
              <Text textStyle="label" lineClamp={1}>
                {peerName}
              </Text>
              <Text textStyle="caption" color="fg.muted" fontVariantNumeric="tabular-nums">
                {statusText}
              </Text>
            </Stack>
          </HStack>
          {/* На телефоне пилюля — основной вид звонка, а плашка со звуком живёт в развёрнутом
              окне: без этой кнопки человек с заблокированным звуком не увидел бы вообще ничего и
              просто сидел бы в тишине. Появляется только когда звук правда заблокирован. */}
          {audioBlocked && (
            <IconButton
              aria-label={t`Turn on sound`}
              size="sm"
              minW="40px"
              h="40px"
              variant="solid"
              colorPalette="orange"
              borderRadius="xl"
              onClick={() => void unblockAudio()}
            >
              <VolumeXIcon />
            </IconButton>
          )}
          <IconButton
            aria-label={micEnabled ? t`Mute microphone` : t`Unmute microphone`}
            size="sm"
            minW="40px"
            h="40px"
            variant={micEnabled ? 'subtle' : 'solid'}
            colorPalette={micEnabled ? 'gray' : 'red'}
            borderRadius="xl"
            onClick={() => setMicEnabled(!micEnabled)}
          >
            {micEnabled ? <MicIcon /> : <MicOffIcon />}
          </IconButton>
          <IconButton
            aria-label={t`End call`}
            size="sm"
            minW="40px"
            h="40px"
            colorPalette="red"
            borderRadius="xl"
            onClick={() => hangup('ended')}
          >
            <PhoneOffIcon />
          </IconButton>
          <IconButton
            aria-label={t`Expand call`}
            size="sm"
            minW="40px"
            h="40px"
            variant="subtle"
            colorPalette="gray"
            borderRadius="xl"
            onClick={() => setWindowMode('window')}
          >
            <MaximizeIcon />
          </IconButton>
        </HStack>
      )}

      {phase === 'active' && windowMode !== 'mini' && (
        <Box
          ref={windowRef}
          position="fixed"
          {...(windowMode === 'full'
            ? { inset: 0 }
            : typeof window !== 'undefined' && window.innerWidth >= 768
              ? CORNER_PROPS[corner]
              : { inset: 0 })}
          zIndex="modal"
          borderRadius={windowMode === 'full' ? 'none' : { base: 'none', md: 'xl' }}
          overflow="hidden"
          boxShadow="2xl"
          display="flex"
          flexDirection="column"
          bg="bg"
          {...(windowMode === 'window'
            ? {
                w: { md: '400px' },
                h: { md: 'auto' },
                css: {
                  overscrollBehavior: 'none',
                  '@media (min-width: 768px)': {
                    resize: 'both',
                    overflow: 'hidden',
                    minWidth: '340px',
                    minHeight: '220px',
                    maxWidth: '80vw',
                    maxHeight: '85vh',
                  },
                },
              }
            : { css: { overscrollBehavior: 'none' } })}
        >
          {/* Thin grab strip: drag the window to any screen corner (desktop, window mode).
              It is also the top edge of the widget whenever the widget is fullscreen, so it
              carries the notch inset — `env()` is 0 on desktop and on the non-edge-to-edge
              Android shell, and non-zero exactly where the widget covers the screen. */}
          <Box
            h={{ base: '22px', md: '14px' }}
            pt="env(safe-area-inset-top, 0px)"
            boxSizing="content-box"
            flexShrink={0}
            bg="bg"
            cursor={{ base: 'default', md: windowMode === 'window' ? 'grab' : 'default' }}
            onPointerDown={onHeaderPointerDown}
            userSelect="none"
            css={{ touchAction: 'none', overscrollBehavior: 'contain' }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box w="36px" h="4px" borderRadius="full" bg="whiteAlpha.400" />
          </Box>
          <Box flex={1} minH={0} display="flex" flexDirection="column">
            <CallStage
              onOpenDeviceMenu={() => {
                void refreshDevices();
                setDeviceMenuOpen(true);
              }}
              call={call}
              variant="floating"
              phase="active"
              duration={duration}
              micEnabled={micEnabled}
              camEnabled={camEnabled}
              onToggleMic={() => setMicEnabled(!micEnabled)}
              onToggleCam={() => setCamEnabled(!camEnabled)}
              onHangup={() => hangup('ended')}
              room={room}
              selfAvatar={selfAvatar}
              fill={windowMode === 'full'}
              headerExtra={
                <>
                  {isDocumentPipSupported() && (
                    <IconButton
                      aria-label={t`Pop out call widget`}
                      size="sm"
                      minW="40px"
                      h="40px"
                      borderRadius="lg"
                      bg="transparent"
                      color="fg.secondary"
                      _hover={{ bg: 'bg.emphasized', color: 'fg' }}
                      onClick={() => void openPip()}
                    >
                      <PipIcon />
                    </IconButton>
                  )}
                  <DeviceMenu
                    devices={devices}
                    onSwitch={(kind, id) => void switchDevice(kind, id)}
                    onOpen={() => void refreshDevices()}
                    open={deviceMenuOpen}
                    onOpenChange={setDeviceMenuOpen}
                  />
                  {/* Разворачивать нечего на телефоне: там окно звонка и так занимает экран
                      целиком в обоих режимах, и кнопка была молчаливой пустышкой. */}
                  {windowMode === 'window' ? (
                    <IconButton
                      aria-label={t`Enter fullscreen`}
                      size="sm"
                      minW="40px"
                      h="40px"
                      borderRadius="lg"
                      bg="transparent"
                      color="fg.secondary"
                      display={{ base: 'none', md: 'inline-flex' }}
                      _hover={{ bg: 'bg.emphasized', color: 'fg' }}
                      onClick={() => setWindowMode('full')}
                    >
                      <MaximizeIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      aria-label={t`Exit fullscreen`}
                      size="sm"
                      minW="40px"
                      h="40px"
                      borderRadius="lg"
                      bg="transparent"
                      color="fg.secondary"
                      display={{ base: 'none', md: 'inline-flex' }}
                      _hover={{ bg: 'bg.emphasized', color: 'fg' }}
                      onClick={() => setWindowMode('window')}
                    >
                      <MinimizeIcon />
                    </IconButton>
                  )}
                  <IconButton
                    aria-label={t`Minimize call`}
                    size="sm"
                    minW="40px"
                    h="40px"
                    borderRadius="lg"
                    bg="transparent"
                    color="fg.secondary"
                    _hover={{ bg: 'bg.emphasized', color: 'fg' }}
                    onClick={() => setWindowMode('mini')}
                  >
                    <ChevronDownIcon />
                  </IconButton>
                </>
              }
            />
          </Box>
        </Box>
      )}

      {/* Missed and declined are the common outcomes at this stage, so the note is also the
          place to try again — otherwise the call ends in a dead end. */}
      {/* Звонок идёт на другом устройстве этого же человека. Подключиться отсюда нельзя — второй
          клиент выбил бы первого из комнаты, и собеседник потерял бы разговор, — поэтому вместо
          тихой драки за комнату предлагаем перенести его сюда, как это делает плеер с колонкой.
          Панель на месте пилюли: и в браузере, и в приложении она попадает в один и тот же угол. */}
      {phase === 'elsewhere' && (
        <HStack
          ref={elsewhereRef}
          position="fixed"
          // Край на телефоне — тот же, что человек выбрал для пилюли: разговор один, и место на
          // экране у него одно. `auto` с противоположной стороны обязателен, иначе панель
          // растягивается от верха до низа.
          top={{ base: mobileAnchor === 'top' ? MOBILE_BAR_TOP : 'auto', md: 'auto' }}
          bottom={{ base: mobileAnchor === 'top' ? 'auto' : MOBILE_BAR_BOTTOM, md: 4 }}
          left={{ base: 4, md: 'auto' }}
          right={4}
          zIndex="modal"
          bg="bg.surface/92"
          backdropFilter={PILL_BACKDROP}
          borderRadius="2xl"
          borderWidth="1px"
          borderColor="border"
          boxShadow="xl"
          pl={3}
          pr={2.5}
          py={2.5}
          gap={3}
        >
          {/* Аватар с именем — ручка панели, ровно как у пилюли. Не вся панель целиком:
              `preventDefault` на pointerdown отобрал бы нажатия у «Move here», трубки и крестика
              рядом. */}
          <HStack
            gap={3}
            minW={0}
            flex={1}
            userSelect="none"
            onPointerDown={onElsewherePointerDown}
            css={{ touchAction: 'none', overscrollBehavior: 'contain' }}
          >
            <Avatar
              size="xs"
              src={call.peer.avatar}
              name={peerName}
              borderRadius="lg"
              css={{ '--avatar-radius': 'var(--chakra-radii-lg)' }}
            />
            <Stack gap={0} minW={0} flex={1}>
              <Text textStyle="label" lineClamp={1}>
                {peerName}
              </Text>
              <Text textStyle="caption" color="fg.muted" lineClamp={1}>
                {t`Call is open on another device`}
              </Text>
            </Stack>
          </HStack>
          <Button
            size="sm"
            minH="40px"
            variant="outline"
            borderRadius="xl"
            flexShrink={0}
            onClick={() => void claim()}
          >
            {t`Move here`}
          </Button>
          <IconButton
            aria-label={t`End call for everyone`}
            size="sm"
            minW="40px"
            h="40px"
            colorPalette="red"
            borderRadius="xl"
            onClick={() => hangup('ended')}
          >
            <PhoneOffIcon />
          </IconButton>
          {/* Третий, безобидный выход: убрать панель именно здесь. Без него единственным способом
              избавиться от неё была красная трубка — то есть оборвать разговор, который в эту
              секунду идёт на другом устройстве. */}
          <IconButton
            aria-label={t`Hide on this device`}
            size="sm"
            minW="32px"
            h="40px"
            bg="transparent"
            color="fg.muted"
            borderRadius="xl"
            _hover={{ bg: 'bg.emphasized', color: 'fg' }}
            onClick={dismissHere}
          >
            <CloseIcon />
          </IconButton>
        </HStack>
      )}

      {phase === 'ended' && (
        <HStack
          position="fixed"
          bottom={{ base: MOBILE_BAR_BOTTOM, md: 6 }}
          left={{ base: 4, md: '50%' }}
          right={{ base: 4, md: 'auto' }}
          transform={{ md: 'translateX(-50%)' }}
          zIndex="modal"
          bg="bg.surface/92"
          backdropFilter={PILL_BACKDROP}
          borderRadius="2xl"
          borderWidth="1px"
          borderColor="border"
          boxShadow="xl"
          pl={4}
          pr={2.5}
          py={2.5}
          gap={4}
          onMouseEnter={() => setEndedHovered(true)}
          onMouseLeave={() => setEndedHovered(false)}
        >
          <Stack gap={0} minW={0} flex={1}>
            <Text textStyle="title">{endedText}</Text>
            <Text textStyle="caption" color="fg.muted" lineClamp={1}>
              {peerName}
              {call.acceptedAt ? ` · ${lastDurationRef.current}` : ''}
            </Text>
          </Stack>
          <Button
            size="sm"
            h="38px"
            variant="outline"
            borderRadius="xl"
            flexShrink={0}
            onClick={() => void start(call.peer, { video: call.withVideo })}
          >
            {t`Call back`}
          </Button>
        </HStack>
      )}
    </Portal>
  );
};
